import { Modal } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../../locales/translations';
import {
  AdditionalCharge,
  AdditionalChargeFields,
  AdditionalChargeRequest,
} from '../interfaces/types';
import { useAdditionalChargesFormLoader } from './Loaders/AdditionalChargesFormLoader';
import AdditionalChargeForm from './AdditionalChargeForm';

export type AdditionalChargeModalMethods = {
  open: (row?: AdditionalCharge) => void;
  close: () => void;
};

type AdditionalChargeModalProps = {
  type: 'INICIO' | 'CONTINUIDAD';
  create: (values: AdditionalChargeRequest) => Promise<void> | void;
  update: (id: number, values: AdditionalChargeRequest) => Promise<void> | void;
};

function AdditionalChargeModal(
  { type, create, update }: AdditionalChargeModalProps,
  ref: Ref<AdditionalChargeModalMethods>,
) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_ADDITIONAL_CHARGES,
  );
  const { data } = useAdditionalChargesFormLoader();

  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [charge, setCharge] = useState<AdditionalCharge>();

  const close = useCallback(() => {
    setCharge(undefined);
    setVisible(false);
  }, []);

  const open = useCallback((row?: AdditionalCharge) => {
    setCharge(row);
    setVisible(true);
  }, []);

  useImperativeHandle(ref, () => ({ open, close }));

  const handleSave = useCallback(
    async (values: AdditionalChargeFields) => {
      if (saving) {
        return;
      }
      setSaving(true);
      const payload: AdditionalChargeRequest = {
        type,
        name: values.name,
        periodId: Number(values.period.value),
        academicLevelId: Number(values.academicLevel.value),
        amount: Number(values.amount),
        integrationCode: values.integrationCode || undefined,
        description: values.description || undefined,
      };
      if (charge?.id) {
        await update(charge.id, payload);
      } else {
        await create(payload);
      }
      setSaving(false);
    },
    [saving, charge, type, create, update],
  );

  return (
    <>
      {!!visible && (
        <Modal isOpen={true} toggle={close}>
          <Container>
            <Row>
              <Col xs={12}>
                <h1 className="text-dark text-center fw-600 fs-20">
                  {charge?.id
                    ? t('modal.update.title')
                    : t('modal.create.title')}
                </h1>
              </Col>
            </Row>
            <AdditionalChargeForm
              {...data}
              saving={saving}
              onCancel={close}
              onSubmit={handleSave}
              defaultValues={{
                name: charge?.name || '',
                period: charge?.period
                  ? { label: charge.period.name, value: charge.period.id }
                  : undefined,
                academicLevel: charge?.academicLevel
                  ? {
                      label: charge.academicLevel.name,
                      value: charge.academicLevel.id,
                    }
                  : undefined,
                amount: charge?.amount?.toString() || '',
                integrationCode: charge?.integrationCode || '',
                description: charge?.description || '',
              }}
            />
          </Container>
        </Modal>
      )}
    </>
  );
}

export default forwardRef(AdditionalChargeModal);
